<template>
  <Modal v-model="dialogShow" :can-close="!isDiscarding && !isFullDeleting">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <h1 class="mb-3">{{ $t('candidate.detail.discard_choice_title', { name: candidateName }) }}</h1>
          <p class="mb-4">{{ $t('candidate.detail.discard_choice_text', { name: candidateName }) }}</p>

          <div class="discard-option">
            <div class="discard-option-body">
              <h4 class="discard-option-title">{{ $t('candidate.detail.discard_option_title') }}</h4>
              <p class="discard-option-text mb-0">
                {{ $t('candidate.detail.discard_option_text', { name: candidateName, id: orderId }) }}
              </p>
            </div>
            <LoadingButton
              class="btn btn-outline-secondary discard-option-action"
              :disabled="isFullDeleting"
              @press="handleDiscard">
              {{ $t('candidate.detail.discard_button') }}
            </LoadingButton>
          </div>

          <div class="discard-option discard-option--danger">
            <div class="discard-option-body">
              <h4 class="discard-option-title">{{ $t('candidate.detail.full_delete_option_title') }}</h4>
              <p class="discard-option-text mb-0">
                {{ $t('candidate.detail.full_delete_option_text', { name: candidateName }) }}
              </p>
            </div>
            <LoadingButton
              class="btn btn-danger discard-option-action"
              :disabled="isDiscarding"
              @press="handleFullDelete">
              {{ $t('candidate.detail.full_delete_button') }}
            </LoadingButton>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn" :disabled="isDiscarding || isFullDeleting" @click="close">
            {{ $t('btn.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>
<script setup lang="ts">
import { Modal, LoadingButton } from '@prospective/lithium';
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import translator from '@/locale/translator';
import { discardCandidate, fullDeleteCandidate } from '@/api/candidates';

const props = defineProps<{
  candidateEntityId: string;
  orderId: number | string;
  candidateName: string;
}>();

const dialogShow = defineModel<boolean>('dialogShow', { default: false });

const emit = defineEmits<{
  discarded: [];
  fullyDeleted: [];
}>();

const toast = useToast();

/* --------------------------------- STATE ----------------------------------- */
const isDiscarding = ref(false);
const isFullDeleting = ref(false);

/* --------------------------------- METHODS -------------------------------- */
const close = () => {
  dialogShow.value = false;
};

const handleDiscard = async (stopLoading: () => void) => {
  isDiscarding.value = true;
  try {
    await discardCandidate(props.candidateEntityId, props.orderId);
    toast.success(translator.get('candidate.detail.discard_success'));
    close();
    emit('discarded');
  } catch (error) {
    console.error('Error discarding candidate:', error);
    toast.error(translator.get('candidate.detail.discard_error'));
  } finally {
    isDiscarding.value = false;
    stopLoading();
  }
};

const handleFullDelete = async (stopLoading: () => void) => {
  isFullDeleting.value = true;
  try {
    await fullDeleteCandidate(props.candidateEntityId);
    toast.success(translator.get('candidate.detail.full_delete_success'));
    close();
    emit('fullyDeleted');
  } catch (error) {
    console.error('Error deleting candidate:', error);
    toast.error(translator.get('candidate.detail.full_delete_error'));
  } finally {
    isFullDeleting.value = false;
    stopLoading();
  }
};
</script>
<style scoped>
.discard-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  margin-bottom: 12px;
}

.discard-option:last-child {
  margin-bottom: 0;
}

.discard-option--danger {
  border-color: #fda29b;
  background: #fffbfa;
}

.discard-option-body {
  flex: 1;
  min-width: 0;
}

.discard-option-title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  margin: 0 0 2px;
}

.discard-option-text {
  font-size: 13px;
  color: #475467;
}

.discard-option-action {
  flex: 0 0 auto;
}
</style>
